import { applyI18n, t } from '../i18n';
import { apiErrorMessage } from '../i18n/apiErrorMessage';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { iconImg } from './icons';
import { bindModalDismiss, createModalShell, refreshModalTitle } from './modalDialogShell';

export interface LinkCopyDialogContent {
  titleKey: string;
  warningKey: string;
  createdKey: string;
  creatingTitleKey: string;
  creatingDetailKey: string;
}

function isFullLinkCopy(value: string, guestUrl: string): boolean {
  return value === guestUrl;
}

export interface LinkCopyDialogOptions {
  onReady?: (controls: { close: () => void; body: HTMLElement }) => void;
  /** Help article anchor. Defaults to information-create guest-link help. */
  helpTopic?: string;
  /** When true (default), closing without copying shows a reminder — for one-time guest links. */
  requireCopyBeforeClose?: boolean;
  copyButtonKey?: string;
  closeButtonKey?: string;
  copiedKey?: string;
  /** Optional secondary action (e.g. SEDR invite email template). */
  secondaryAction?: {
    labelKey: string;
    onClick: (guestUrl: string, helpers: { markDelivered: () => void }) => void;
  };
}

/**
 * Shows a create-then-copy guest link dialog.
 * The returned promise resolves only when the dialog is closed (success or error).
 */
export async function showLinkCopyDialog(
  createLink: () => Promise<string>,
  content: LinkCopyDialogContent,
  options?: LinkCopyDialogOptions,
): Promise<void> {
  let copied = false;
  let guestUrl = '';
  const requireCopyBeforeClose = options?.requireCopyBeforeClose ?? true;
  const copyButtonKey = options?.copyButtonKey ?? 'info.owner.copyLink';
  const closeButtonKey = options?.closeButtonKey ?? 'info.owner.dialogClose';
  const copiedKey = options?.copiedKey ?? 'info.owner.copied';

  const shell = createModalShell({
    titleKey: content.titleKey,
    titleId: 'link-copy-dialog-title',
    helpTopic: options?.helpTopic ?? dialogHelpTopic('linkCopy'),
  });

  shell.body.innerHTML = `
    <div class="crypto-busy-panel" role="status" aria-live="polite" aria-busy="true">
      <span class="crypto-busy-panel__spinner" aria-hidden="true"></span>
      <div class="crypto-busy-panel__text">
        <strong data-i18n="${content.creatingTitleKey}"></strong>
        <p class="muted" data-i18n="${content.creatingDetailKey}"></p>
      </div>
    </div>
  `;

  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const abort = new AbortController();
  let showingCopied = false;
  let showingError = false;
  let lastErrorMessage = '';
  let closeAttempts = 0;
  let unbindDismiss: (() => void) | null = null;
  let settled = false;
  let resolveClosed: (() => void) | null = null;
  const closed = new Promise<void>((resolve) => {
    resolveClosed = resolve;
  });

  const refreshLocale = () => {
    refreshModalTitle(shell, { titleKey: content.titleKey });
    if (showingError) {
      const errorEl = shell.body.querySelector('.error-text');
      if (errorEl) {
        errorEl.textContent = lastErrorMessage || t('status.error');
      }
      applyI18n(shell.body);
      return;
    }

    if (showingCopied) {
      const copyLabel = shell.body.querySelector('#link-copy-copy [data-i18n], #link-copy-copy span');
      if (copyLabel instanceof HTMLElement) {
        copyLabel.textContent = t(copiedKey);
      }
    }

    applyI18n(shell.body);
  };

  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const closeDialog = () => {
    if (settled) {
      return;
    }
    settled = true;
    unbindLocale();
    unbindDismiss?.();
    abort.abort();
    shell.backdrop.remove();
    resolveClosed?.();
  };

  const tryClose = () => {
    if (showingError) {
      closeDialog();
      return;
    }

    if (requireCopyBeforeClose && !copied) {
      const copyWarning = shell.body.querySelector('#link-copy-copy-warning') as HTMLParagraphElement | null;
      if (copyWarning) {
        copyWarning.hidden = false;
      }
      closeAttempts += 1;
      if (closeAttempts >= 2) {
        closeDialog();
      }
      return;
    }

    closeDialog();
  };

  unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: tryClose,
  });

  try {
    guestUrl = await createLink();
  } catch (cause) {
    console.error('Create guest link failed', cause);
    showingError = true;
    lastErrorMessage = apiErrorMessage(cause);
    shell.body.innerHTML = `<p class="error-text"></p>`;
    const errorEl = shell.body.querySelector('.error-text');
    if (errorEl) {
      errorEl.textContent = lastErrorMessage;
    }
    shell.actions.innerHTML = `
      <button type="button" class="button primary" id="link-copy-close-error">
        <span data-i18n="${closeButtonKey}"></span>
      </button>
    `;
    applyI18n(shell.dialog);
    shell.actions.querySelector('#link-copy-close-error')?.addEventListener('click', closeDialog);
    await closed;
    return;
  }

  shell.body.innerHTML = `
    <div class="warning-panel">
      <span class="warning-panel__icon"></span>
      <span data-i18n="${content.warningKey}"></span>
    </div>
    <p class="muted" data-i18n="${content.createdKey}"></p>
    <div class="link-box">
      <input type="text" id="link-copy-url" readonly />
      <button type="button" class="button primary" id="link-copy-copy">
        <span data-i18n="${copyButtonKey}"></span>
      </button>
    </div>
    ${requireCopyBeforeClose
      ? '<p class="error-text" id="link-copy-copy-warning" hidden data-i18n="info.owner.copyReminder"></p>'
      : ''}
  `;

  shell.actions.innerHTML = `
    ${options?.secondaryAction
      ? `<button type="button" class="button subtle" id="link-copy-secondary">
          <span data-i18n="${options.secondaryAction.labelKey}"></span>
        </button>`
      : ''}
    <button type="button" class="button ghost" id="link-copy-close">
      <span data-i18n="${closeButtonKey}"></span>
    </button>
  `;

  shell.body.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));
  applyI18n(shell.dialog);
  options?.onReady?.({ close: closeDialog, body: shell.body });

  const urlInput = shell.body.querySelector('#link-copy-url') as HTMLInputElement;
  urlInput.value = guestUrl;

  const copyWarning = shell.body.querySelector('#link-copy-copy-warning') as HTMLParagraphElement | null;
  const copyLabel = shell.body.querySelector('#link-copy-copy [data-i18n]');

  const showCopiedFeedback = () => {
    showingCopied = true;
    if (copyLabel instanceof HTMLElement) {
      copyLabel.textContent = t(copiedKey);
      window.setTimeout(() => {
        showingCopied = false;
        copyLabel.setAttribute('data-i18n', copyButtonKey);
        applyI18n(shell.body);
      }, 2000);
    }
  };

  const markCopied = () => {
    if (copied) {
      return;
    }

    copied = true;
    if (copyWarning) {
      copyWarning.hidden = true;
    }
    showCopiedFeedback();
  };

  const noteClipboardCopy = (event: ClipboardEvent) => {
    const fromInput =
      event.target === urlInput ||
      (event.target instanceof Node && urlInput.contains(event.target));
    if (!fromInput && event.target !== document && event.target !== document.body) {
      return;
    }

    const start = urlInput.selectionStart ?? 0;
    const end = urlInput.selectionEnd ?? 0;
    const selected = urlInput.value.slice(start, end);
    const clip = event.clipboardData?.getData('text/plain') ?? selected;
    if (isFullLinkCopy(clip, guestUrl) || isFullLinkCopy(selected, guestUrl)) {
      markCopied();
    }
  };

  urlInput.addEventListener('copy', noteClipboardCopy, { signal: abort.signal });
  document.addEventListener('copy', noteClipboardCopy, { signal: abort.signal });

  shell.body.querySelector('#link-copy-copy')?.addEventListener(
    'click',
    async () => {
      await navigator.clipboard.writeText(guestUrl);
      markCopied();
    },
    { signal: abort.signal },
  );

  shell.actions.querySelector('#link-copy-close')?.addEventListener(
    'click',
    tryClose,
    { signal: abort.signal },
  );

  shell.actions.querySelector('#link-copy-secondary')?.addEventListener(
    'click',
    () => {
      options?.secondaryAction?.onClick(guestUrl, { markDelivered: markCopied });
    },
    { signal: abort.signal },
  );

  await closed;
}
